import React, { useEffect } from 'react';
import { CheckCircle, XCircle, Info, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastStackProps {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}

/* 旧 styled-components 定義。keyframes は globals.css の `msc-toast-slide-in`
 * （Toast.tsx の slideIn と同内容なので統合）を参照する。 */
const VIEWPORT =
  'fixed top-6 right-6 z-[10001] flex flex-col gap-3 ' +
  'max-w-[min(500px,calc(100vw-3rem))] pointer-events-none ' +
  'max-[480px]:left-3 max-[480px]:right-3 max-[480px]:max-w-none';

const ITEM_CONTAINER =
  'flex items-center gap-3 px-5 py-3.5 rounded-tasky-sm ' +
  'shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05)] ' +
  'animate-[msc-toast-slide-in_0.3s_ease-out] ' +
  'min-w-[300px] max-w-full pointer-events-auto max-[480px]:min-w-0';

const ITEM_TYPE: Record<ToastType, string> = {
  success: 'bg-emerald-500 text-white', // #10B981
  error: 'bg-red-500 text-white', // #EF4444
  info: 'bg-blue-500 text-white', // #3B82F6
};

const ICON_WRAPPER = 'flex items-center justify-center shrink-0';

const MESSAGE = 'text-[0.9375rem] font-medium leading-[1.5] flex-1 whitespace-pre-line';

const CLOSE_BUTTON =
  'flex items-center justify-center shrink-0 bg-transparent border-0 ' +
  'text-inherit opacity-80 cursor-pointer p-0.5 hover:opacity-100';

/**
 * 画面右上に固定表示するトーストの縦スタック。
 * 個々のトーストは自分の duration で自動的に消え、× でも閉じられる。
 */
const ToastStack: React.FC<ToastStackProps> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div className={VIEWPORT} role="status" aria-live="polite">
      {toasts.map((toast) => (
        <ToastRow key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

const ToastRow: React.FC<{ toast: ToastItem; onDismiss: (id: number) => void }> = ({ toast, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <XCircle className="w-5 h-5" />;
      case 'info':
        return <Info className="w-5 h-5" />;
      default:
        return null;
    }
  };

  return (
    <div className={`${ITEM_CONTAINER} ${ITEM_TYPE[toast.type] ?? ''}`}>
      <div className={ICON_WRAPPER}>{getIcon()}</div>
      <span className={MESSAGE}>{toast.message}</span>
      <button className={CLOSE_BUTTON} onClick={() => onDismiss(toast.id)} aria-label="通知を閉じる" type="button">
        <X size={14} />
      </button>
    </div>
  );
};

export default ToastStack;
